/**
 * Диапазон Shift-клика по чекбоксам библиотеки.
 * Список — только строки с чекбоксом, уже в порядке на экране (фильтр и сортировка снаружи).
 *
 * Якорь — последняя строка, включённая обычным кликом. Span — последний Shift-кусок:
 * следующий Shift проводит его заново и снимает строки, которые из куска вышли.
 * Обычный клик, который включает строку, якорь переносит и кусок замораживает.
 */

export type LibraryShiftGesture = {
  anchor: string | null
  span: readonly string[]
}

/** От якоря до кликнутого включительно, в порядке экрана. null — якоря в этом списке нет. */
export function libraryShiftRange<T extends { key: string }>(
  items: readonly T[],
  anchorKey: string | null,
  targetKey: string,
): T[] | null {
  if (anchorKey == null) return null
  const from = items.findIndex((item) => item.key === anchorKey)
  const to = items.findIndex((item) => item.key === targetKey)
  if (from < 0 || to < 0) return null
  const start = Math.min(from, to)
  const end = Math.max(from, to)
  return items.slice(start, end + 1)
}

/**
 * Обычный клик. selected — строка после клика включена.
 * Включение переносит якорь и забывает прежний кусок, не снимая его строки.
 * Снятие с якоря оставляет Shift без точки отсчёта.
 */
export function libraryShiftPlainClick(
  gesture: LibraryShiftGesture,
  key: string,
  selected: boolean,
): LibraryShiftGesture {
  if (selected) return { anchor: key, span: [] }
  if (gesture.anchor === key) return { anchor: null, span: [] }
  return gesture
}

export type LibraryShiftClick<T> = {
  gesture: LibraryShiftGesture
  /** От якоря к клику: в таком порядке строки добираются, пока есть места. */
  select: T[]
  /** Ключи прошлого куска, которых в новом срезе нет. */
  dropKeys: string[]
}

/** null — якоря нет, Shift ничего не делает. */
export function libraryShiftClick<T extends { key: string }>(
  items: readonly T[],
  gesture: LibraryShiftGesture,
  targetKey: string,
): LibraryShiftClick<T> | null {
  const slice = libraryShiftRange(items, gesture.anchor, targetKey)
  if (!slice || gesture.anchor == null) return null
  const span = slice.map((item) => item.key)
  const keep = new Set(span)
  const dropKeys = gesture.span.filter((key) => !keep.has(key))
  const from = items.findIndex((item) => item.key === gesture.anchor)
  const to = items.findIndex((item) => item.key === targetKey)
  const step = from <= to ? 1 : -1
  const select: T[] = []
  for (let index = from; step > 0 ? index <= to : index >= to; index += step) {
    const item = items[index]
    if (item) select.push(item)
  }
  return { gesture: { anchor: gesture.anchor, span }, select, dropKeys }
}
